// Регион сайта определяется только по хосту запроса и только по белому списку:
// IP, cookie и выбор пользователя не меняют контент внутри одного URL.
// Код региона совпадает с cities.slug в CMS.
export type RegionCode = 'msk' | 'spb' | 'nnov'

export const DEFAULT_REGION: RegionCode = 'msk'

// msk.expertakpp.ru — поддомен под рекламу (в robots закрыт nginx'ом), контент московский.
const REGION_BY_HOST: Readonly<Record<string, RegionCode>> = {
  'expertakpp.ru': 'msk',
  'msk.expertakpp.ru': 'msk',
  'nnov.expertakpp.ru': 'nnov',
  'spb.expertakpp.ru': 'spb',
}

export const REGION_CODES: readonly RegionCode[] = ['msk', 'spb', 'nnov']

const isRegionCode = (value: string): value is RegionCode =>
  (REGION_CODES as readonly string[]).includes(value)

// Регион из параметра запроса к API (?region=spb); null — не задан или неизвестен.
export const parseRegion = (value: unknown): RegionCode | null => {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : ''
  return isRegionCode(normalized) ? normalized : null
}

// "SPB.ExpertAKPP.ru:443" -> "spb.expertakpp.ru"; из списка X-Forwarded-Host берётся первый.
export const normalizeHost = (rawHost: string | null | undefined): string => {
  const firstHost = (rawHost ?? '').split(',')[0] ?? ''
  return firstHost.trim().toLowerCase().replace(/:\d+$/, '').replace(/\.$/, '')
}

// null — хост не из белого списка (в проде такие запросы отсекает nginx).
// Для локальной разработки: localhost — Москва, spb.localhost — Петербург.
export const regionByHost = (rawHost: string | null | undefined): RegionCode | null => {
  const host = normalizeHost(rawHost)
  const knownRegion = REGION_BY_HOST[host]
  if (knownRegion) {
    return knownRegion
  }

  if (host === 'localhost' || host === '127.0.0.1') {
    return DEFAULT_REGION
  }

  const localSubdomain = host.endsWith('.localhost') ? host.slice(0, -'.localhost'.length) : ''
  return isRegionCode(localSubdomain) ? localSubdomain : null
}

// Origin региона для canonical, og:url и JSON-LD. Строится от NUXT_PUBLIC_SITE_URL
// (origin Москвы), а не от заголовков запроса: https://expertakpp.ru -> https://spb.expertakpp.ru.
export const regionOrigin = (region: RegionCode, mainSiteUrl: string): string => {
  const mainUrl = new URL(mainSiteUrl)
  if (region === DEFAULT_REGION) {
    return mainUrl.origin
  }

  return `${mainUrl.protocol}//${region}.${mainUrl.host}`
}
